import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { convertWattHourToMegaWattHour } from "@/helpers/common.helpers";
import { getBaseUrl } from "@/helpers/fetch.helpers";
import { getJwtToken } from "@/helpers/storage.helpers";
import type {
  ContractableEnergy,
  ContractableEnergyParams,
  ContractableEnergyTransformResponse,
  DisabledProfileCreatorResponse,
  DisabledProfileCreatorResultant,
  DisabledProfileCreatorResultantParams,
  DisableProfileCreatorBody,
} from "@/pages/Energy";

export const profileCreatorApi = createApi({
  reducerPath: "profileCreatorApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseUrl("/profile-creator/"),
    prepareHeaders: (headers) => {
      headers.set("authorization", `Bearer ${getJwtToken()}`);
      return headers;
    },
  }),
  tagTypes: ["ProfileCreatorDisabledResultant"],
  endpoints: (build) => ({
    getDisabledProfileCreatorResultant: build.query<
      DisabledProfileCreatorResultant[],
      DisabledProfileCreatorResultantParams
    >({
      query: ({ deviceID, from, to, origins }) => {
        return {
          url: "/locks/resultant",
          params: {
            deviceID,
            start: from,
            end: to,
            origins: origins.join(","),
          },
        };
      },
      transformResponse: (response: {
        resultants: DisabledProfileCreatorResponse[];
      }): DisabledProfileCreatorResultant[] => {
        return response.resultants.map((resultant) => {
          return {
            from: resultant.start,
            to: resultant.end,
            locked: resultant.locked,
          };
        });
      },
      providesTags: ["ProfileCreatorDisabledResultant"],
    }),
    toggleProfileCreator: build.mutation<unknown, DisableProfileCreatorBody>({
      query: (body) => {
        return {
          url: "locks",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["ProfileCreatorDisabledResultant"],
    }),
    getAvailableEnergy: build.query<
      ContractableEnergyTransformResponse[],
      ContractableEnergyParams
    >({
      query: ({ deviceID, from, to }) => {
        return {
          url: "/contractable",
          params: {
            deviceID,
            start: from,
            end: to,
          },
        };
      },
      transformResponse: (response: {
        contractableEnergy: ContractableEnergy[];
      }): ContractableEnergyTransformResponse[] => {
        return response.contractableEnergy.map((contractableEnergyBlock) => {
          return {
            from: contractableEnergyBlock.start,
            to: contractableEnergyBlock.end,
            maxAdditionalEnergy: convertWattHourToMegaWattHour(
              contractableEnergyBlock.maxAdditionalWh ?? 0
            ),
            minAdditionalEnergy: convertWattHourToMegaWattHour(
              contractableEnergyBlock.minAdditionalWh ?? 0
            ),
            scheduledEnergy: convertWattHourToMegaWattHour(
              contractableEnergyBlock.scheduledWh ?? 0
            ),
            maxContractableEnergy: convertWattHourToMegaWattHour(
              contractableEnergyBlock.maxContractableWh ?? 0
            ),
            minContractableEnergy: convertWattHourToMegaWattHour(
              contractableEnergyBlock.minContractableWh ?? 0
            ),
          };
        });
      },
    }),
  }),
});

export const {
  useGetDisabledProfileCreatorResultantQuery,
  useToggleProfileCreatorMutation,
  useGetAvailableEnergyQuery,
} = profileCreatorApi;
